import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { logSecurityEvent, checkRateLimit } from '@/lib/security';
import { useSecurityMonitoring } from '@/hooks/useSecurityMonitoring';
import { securityUtils } from '@/lib/securityValidation';
import { User, Session } from '@supabase/supabase-js';

interface SecurityContextType {
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  secureAction: (action: () => Promise<any>, actionName: string) => Promise<any>;
  checkPermission: (resource: string, action: string) => boolean;
  securityMetrics: {
    failedAttempts: number;
    suspiciousActivity: boolean;
    isRateLimited: boolean;
  };
  checkAuthRateLimit: (email: string, ip?: string) => Promise<boolean>;
  logFailedAuth: (email: string, attemptType?: 'login' | 'otp' | 'token', failureReason?: string) => Promise<void>;
}

const SecurityContext = createContext<SecurityContextType | undefined>(undefined);

export const useSecurityContext = () => {
  const context = useContext(SecurityContext);
  if (context === undefined) {
    throw new Error('useSecurityContext must be used within a SecurityProvider');
  }
  return context;
};

interface SecurityProviderProps {
  children: React.ReactNode;
}

export const SecurityProvider: React.FC<SecurityProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const { 
    metrics, 
    checkAuthRateLimit, 
    logFailedAuth,
    clearMetrics
  } = useSecurityMonitoring();

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);

        // Log authentication events for security monitoring
        if (event === 'SIGNED_IN') {
          setTimeout(() => {
            logSecurityEvent('user_signed_in', 'auth_session', session?.user?.id, {
              email: session?.user?.email,
              auth_provider: session?.user?.app_metadata?.provider
            });
          }, 0);
        } else if (event === 'SIGNED_OUT') {
          setTimeout(() => {
            logSecurityEvent('user_signed_out', 'auth_session');
          }, 0);
        } else if (event === 'TOKEN_REFRESHED') {
          setTimeout(() => {
            logSecurityEvent('token_refreshed', 'auth_session', session?.user?.id);
          }, 0);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  /**
   * Executes an action with security checks and logging
   */
  const secureAction = async (action: () => Promise<any>, actionName: string): Promise<any> => {
    try {
      // Rate limiting check
      if (!checkRateLimit(`action_${actionName}`, 10, 60000)) { // 10 requests per minute
        throw new Error('Rate limit exceeded. Please try again later.');
      }

      // Log the action attempt
      await logSecurityEvent('action_attempted', 'user_action', user?.id, {
        action_name: actionName,
        timestamp: new Date().toISOString()
      });

      const result = await action();

      // Log successful action
      await logSecurityEvent('action_completed', 'user_action', user?.id, {
        action_name: actionName,
        success: true,
        timestamp: new Date().toISOString()
      });

      return result;
    } catch (error) {
      // Log failed action
      await logSecurityEvent('action_failed', 'user_action', user?.id, {
        action_name: actionName,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      });

      throw error;
    }
  };

  /**
   * Checks if user has permission for a specific resource and action
   */
  const checkPermission = (resource: string, action: string): boolean => {
    // Basic permission checks - extend as needed
    if (!user) {
      return false; // Unauthenticated users have no permissions
    }

    // All authenticated users can read public resources
    if (resource === 'public' && action === 'read') {
      return true;
    }

    // Users can manage their own resources
    if (resource === 'own_profile' || resource === 'own_data') {
      return true;
    }

    // Add more specific permission logic here
    // This could be extended to use role-based access control

    return false;
  };

  const contextValue: SecurityContextType = {
    user,
    session,
    isAuthenticated: !!user,
    isLoading,
    secureAction,
    checkPermission,
    securityMetrics: metrics,
    checkAuthRateLimit,
    logFailedAuth
  };

  return (
    <SecurityContext.Provider value={contextValue}>
      {children}
    </SecurityContext.Provider>
  );
};

/**
 * HOC to protect components that require authentication
 */
export const withSecurityProtection = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  requiredPermission?: { resource: string; action: string }
) => {
  return (props: P) => {
    const { isAuthenticated, isLoading, checkPermission } = useSecurityContext();

    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (!isAuthenticated) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center space-y-4">
            <h2 className="text-xl font-semibold">Authentication Required</h2>
            <p className="text-muted-foreground">Please log in to access this resource.</p>
          </div>
        </div>
      );
    }

    if (requiredPermission && !checkPermission(requiredPermission.resource, requiredPermission.action)) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center space-y-4">
            <h2 className="text-xl font-semibold">Access Denied</h2>
            <p className="text-muted-foreground">You don't have permission to access this resource.</p>
          </div>
        </div>
      );
    }

    return <WrappedComponent {...props} />;
  };
};

/**
 * Hook for secure form submissions
 */
export const useSecureForm = () => {
  const { secureAction } = useSecurityContext();

  const submitForm = async (
    formData: Record<string, any>,
    submitAction: (data: Record<string, any>) => Promise<any>,
    formName: string
  ) => {
    return secureAction(async () => {
      // Validate and sanitize form data
      const { validateFormData } = await import('@/lib/security');
      const { isValid, errors, sanitized } = validateFormData(formData);

      if (!isValid) {
        throw new Error(`Validation failed: ${errors.join(', ')}`);
      }

      return submitAction(sanitized);
    }, `form_submit_${formName}`);
  };

  return { submitForm };
};